import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import "vuetify/styles";
import "@mdi/font/css/materialdesignicons.css";
import { h } from "vue";
import type { IconProps } from "vuetify";

import ChevronLeft16 from "@carbon/icons-vue/es/chevron--left/16";
import ChevronRight16 from "@carbon/icons-vue/es/chevron--right/16";
import ChevronDown16 from "@carbon/icons-vue/es/chevron--down/16";
import PageFirst16 from "@carbon/icons-vue/es/page--first/16";
import PageLast16 from "@carbon/icons-vue/es/page--last/16";
import ChevronSort16 from "@carbon/icons-vue/es/chevron--sort/16";
import Checkmark16 from "@carbon/icons-vue/es/checkmark/16";
import Error16 from "@carbon/icons-vue/es/error/16";
import Warning16 from "@carbon/icons-vue/es/warning/16";
import Information16 from "@carbon/icons-vue/es/information/16";
import RadioButton20 from "@carbon/icons-vue/es/radio-button/20";
import RadioButtonChecked20 from "@carbon/icons-vue/es/radio-button--checked/20";

// Custom iconset for font-based MDI icons
const customMDI: any = {
	component: (props: IconProps) => h("i", { class: `mdi ${props.icon}` }),
};

// Wrap Carbon icons as functional components for Vuetify alias compatibility
const carbonAlias = (icon: any) => () => h(icon);

export default createVuetify({
	components,
	directives,
	icons: {
		defaultSet: "mdi",
		aliases: {
			prev: carbonAlias(ChevronLeft16),
			next: carbonAlias(ChevronRight16),
			first: carbonAlias(PageFirst16),
			last: carbonAlias(PageLast16),
			dropdown: carbonAlias(ChevronDown16),
			sortAsc: carbonAlias(ChevronSort16),
			sortDesc: carbonAlias(ChevronSort16),
			success: carbonAlias(Checkmark16),
			error: carbonAlias(Error16),
			warning: carbonAlias(Warning16),
			info: carbonAlias(Information16),
			treeviewExpand: carbonAlias(ChevronRight16),
			treeviewCollapse: carbonAlias(ChevronDown16),
			radioOn: carbonAlias(RadioButtonChecked20),
			radioOff: carbonAlias(RadioButton20),
		},
		sets: {
			mdi: customMDI,
		},
	},
	display: {
		mobileBreakpoint: "md",
	},
	theme: {
		unimportant: true,
		defaultTheme: "light",
		themes: {
			light: {
				dark: false,
				colors: {
					primary: "#307D2B",
					secondary: "#6C727E",
					accent: "#307D2B",
					error: "#F04437",
					warning: "#FF982E",
					success: "#11AD64",
					info: "#2C89ED",
					danger: "#F04437",
					inactive: "#6C727E",
					anchor: "#307D2B",
				},
			},
			dark: {
				dark: true,
				colors: {
					primary: "#307D2B",
					secondary: "#6C727E",
					accent: "#307D2B",
					error: "#F04437",
					warning: "#FF982E",
					success: "#11AD64",
					info: "#2C89ED",
					danger: "#F04437",
					inactive: "#6C727E",
					anchor: "#307D2B",
				},
			},
		},
	},
	defaults: {
		VBtn: {
			class: "sgnl-text-none",
		},
		VListItemTitle: {
			style: "font-family: var(--sgnl-font-family-text); font-size: var(--sgnl-font-size-paragraph-s); line-height: var(--sgnl-font-line-height-paragraph-s);",
		},
		VCard: {
			elevation: 0,
		},
		VSheet: {
			elevation: 0,
			rounded: "lg",
		},
		VAppBar: {
			elevation: 0,
			rounded: 0,
		},
		VChip: {},
		VTextField: {
			variant: "outlined",
			density: "compact",
			persistentPlaceholder: true,
		},
		VTextarea: {
			variant: "outlined",
			density: "compact",
			persistentPlaceholder: true,
		},
		VSelect: {
			variant: "outlined",
			density: "compact",
			persistentPlaceholder: true,
		},
		VAutocomplete: {
			variant: "outlined",
			density: "compact",
			persistentPlaceholder: true,
		},
		VDialog: {
			rounded: "lg",
		},
		VMenu: {
			rounded: "lg",
		},
		VList: {
			rounded: "lg",
		},
		VAlert: {
			variant: "outlined",
			density: "compact",
		},
		VTooltip: {
			location: "top",
			openDelay: 250,
		},
		VSnackbar: {
			location: "top",
		},
		VSlider: {
			showTicks: "always",
		},
		VProgressLinear: {
			rounded: true,
			color: "primary",
			bgColor: "var(--sgnl-brand-background-inverse)",
			bgOpacity: 1,
		},
		VProgressCircular: {
			indeterminate: true,
			size: 20,
			width: 2,
		},
		VCheckbox: {
			hideDetails: true,
			density: "compact",
		},
		VSwitch: {
			color: "primary",
			hideDetails: "auto",
			density: "compact",
		},
		VRadio: {
			ripple: false,
			color: "primary",
		},
		VRadioGroup: {
			hideDetails: "auto",
		},
		VBtnToggle: {
			variant: "outlined",
		},
		VExpansionPanels: {
			rounded: "lg",
			variant: "accordion",
		},
		VSkeletonLoader: {
			elevation: 0,
		},
	},
});
